import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { CollectiveManagementSocietyUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { FormControl } from "@mui/material";

export type CollectiveManagementSocietyUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  acronym: string;
  acronymSelectedValue?: { displayId: string; id: string } | null;
  onAcronymChange?: (value: string) => void;
  acronymError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountryChange?: (value: string) => void;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  collectiveManagementSocietyUpdateStore: CollectiveManagementSocietyUpdateStore;
};

export const CollectiveManagementSocietyUpdateForm: React.FC<CollectiveManagementSocietyUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      acronym = "",
      acronymError,
      onAcronymChange = () => {},
      onCountrySelected = () => {},
      countryOptions = [],
      countrySelectedValue = null,
      countryError,
      onCountryChange = () => {},
      collectiveManagementSocietyUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="CollectiveManagementSociety"
              mode="update"
              values={{}}
              options={{ country: countryOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "acronym":
                    onAcronymChange?.(valor);
                    break;
                  case "country":
                    onCountrySelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={collectiveManagementSocietyUpdateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupCollectiveManagementSocietyLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(
                      collectiveManagementSocietyUpdateStore.warnings["name"]
                    )
                  }
                  helperText={
                    nameError ||
                    collectiveManagementSocietyUpdateStore.warnings["name"]
                      ?.message
                  }
                  label={langStore.CollectiveManagementSocietyNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={
                    langStore.CollectiveManagementSocietyNameDescription
                  }
                />
                <InputString
                  value={acronym}
                  error={Boolean(acronymError)}
                  warning={
                    !acronymError &&
                    Boolean(
                      collectiveManagementSocietyUpdateStore.warnings["acronym"]
                    )
                  }
                  helperText={
                    acronymError ||
                    collectiveManagementSocietyUpdateStore.warnings["acronym"]
                      ?.message
                  }
                  label={langStore.CollectiveManagementSocietyAcronymLabel}
                  onChange={(value: string) => onAcronymChange(value)}
                  description={
                    langStore.CollectiveManagementSocietyAcronymDescription
                  }
                />
                <InputReference
                  id="country"
                  label={langStore.CollectiveManagementSocietyCountryLabel}
                  error={Boolean(countryError)}
                  helperText={
                    countryError ||
                    collectiveManagementSocietyUpdateStore.warnings["country"]
                      ?.message
                  }
                  selectedValue={countrySelectedValue}
                  onChange={(value: string) => onCountryChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCountrySelected(value)
                  }
                  options={countryOptions}
                  description={
                    langStore.CollectiveManagementSocietyCountryDescription
                  }
                  canCreate={rootStore.authStore.hasPermissions(
                    "country_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CountryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CountryCreate
                          countryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCountry"
                      cancelMethod="cancelNewCountry"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
